import { useSelector } from 'react-redux';
import type { RootState } from '../store';
import { eClientFeaturePermissions } from '../common/Components/FeaturesRestriction/Enums/eClientFeaturePermissions';
import {
  enabledFeaturesDictionarySelector,
  enabledFeaturesSelector
} from '../store/selectors/isFeatureSupportedSelector';
import { globalConfigEnabledFeatures } from '../config';

export type ClientFeature = {
  value: number;
  name?: string;
};

export type GlobalConfigFeaturesKeys = keyof typeof globalConfigEnabledFeatures;
export type ClientFeaturesKeys = keyof typeof eClientFeaturePermissions;

function useClientFeatures() {
  const enabledFeatures = useSelector<RootState, number[]>(enabledFeaturesSelector);
  const enabledFeaturesDictionary = useSelector<RootState, Record<number, boolean>>(enabledFeaturesDictionarySelector);

  const isFeatureEnabled = (feature: ClientFeature | ClientFeature[]) => {
    if (Array.isArray(feature)) {
      return feature.every(({ value }) => Boolean(enabledFeaturesDictionary[value]));
    }
    return Boolean(enabledFeaturesDictionary[feature.value]);
  };

  const isFeatureEnabledByKey = (featureKey: ClientFeaturesKeys) => {
    const feature = eClientFeaturePermissions[featureKey];
    return isFeatureEnabled(feature);
  };

  const isGlobalFeatureEnabledByKey = (featureKey?: GlobalConfigFeaturesKeys) => {
    if (featureKey === undefined) {
      return false;
    }
    return Boolean(globalConfigEnabledFeatures[featureKey]);
  };

  return {
    isFeatureEnabled,
    isFeatureEnabledByKey,
    isGlobalFeatureEnabledByKey,
    enabledFeatures,
    clientFeatures: eClientFeaturePermissions
  };
}

export default useClientFeatures;
